import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, AlertTriangle } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";
import { CALENDLY_URL } from "../data/content";

const WEEKS = ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10", "W11", "W12", "W13"];
const OPENING = [42.0, 45.3, 39.1, 36.8, 41.5, 44.2, 38.9, 33.4, 30.1, 35.6, 39.8, 43.5, 47.2];
const INFLOWS = [12.5, 8.0, 15.2, 18.4, 9.6, 7.3, 11.8, 14.2, 16.5, 13.1, 9.4, 12.6, 15.8];
const OUTFLOWS = [9.2, 14.2, 17.5, 13.7, 6.9, 12.6, 17.3, 17.5, 11.0, 8.9, 5.7, 8.9, 12.4];

const fmt = (n) => `₹${n.toFixed(1)}L`;

const AR_ROWS = [
  { client: "Nalanda Foods", invoice: "INV-1042", date: "12 Aug", days: 62, amount: "₹4.2L", status: "Overdue", week: "W2" },
  { client: "Brightside Retail", invoice: "INV-1058", date: "28 Aug", days: 46, amount: "₹6.8L", status: "Follow-up sent", week: "W3" },
  { client: "Orbit Software", invoice: "INV-1071", date: "09 Sep", days: 34, amount: "₹3.1L", status: "On track", week: "W4" },
  { client: "Meridian Hotels", invoice: "INV-1084", date: "21 Sep", days: 22, amount: "₹8.5L", status: "On track", week: "W6" },
  { client: "Kaveri Textiles", invoice: "INV-1093", date: "02 Oct", days: 11, amount: "₹2.4L", status: "On track", week: "W7" },
];

export default function CashflowTool() {
  let running = 0;
  const closing = WEEKS.map((_, i) => {
    running = OPENING[i] + INFLOWS[i] - OUTFLOWS[i];
    return running;
  });

  return (
    <main className="pt-32 pb-16" data-testid="cashflow-tool-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp y={12}>
          <Link to="/#resources" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-green transition-colors duration-200 mb-10" data-testid="tool-back-link">
            <ArrowLeft size={13} /> All resources
          </Link>
        </FadeUp>
        <p className="font-mono text-lg uppercase tracking-[0.25em] text-ink-mid mb-5">Working model &amp; guide · Free</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-ink mb-6">
          <RevealLine animateOnLoad>13-Week Cash Flow <span className="italic text-green">Forecast.</span></RevealLine>
        </h1>
        <FadeUp className="max-w-2xl text-ink-mid leading-relaxed mb-16">
          The exact model CFOSutra builds for every client. One quarter, week by week, updated every Monday. Below is a working mockup with real-shaped data — and the AR aging schedule that feeds it.
        </FadeUp>

        <FadeUp className="bg-white rounded-2xl overflow-hidden mb-8">
          <div className="px-8 pt-8 pb-4 flex items-center justify-between">
            <h2 className="font-serif text-2xl text-ink">The 13-week tracker</h2>
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber"><AlertTriangle size={12} /> Amber = crunch week</span>
          </div>
          <div className="overflow-x-auto" data-testid="cashflow-table">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-y border-rule bg-green-lt/50 text-left">
                  <th className="px-6 py-3 font-medium uppercase tracking-wider text-ink-mid sticky left-0 bg-green-lt">Line</th>
                  {WEEKS.map((w) => <th key={w} className="px-4 py-3 font-medium uppercase tracking-wider text-ink-mid text-right">{w}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-rule">
                  <td className="px-6 py-3 text-ink-mid sticky left-0 bg-white">Opening cash</td>
                  {OPENING.map((v, i) => <td key={i} className="px-4 py-3 text-right text-ink">{fmt(v)}</td>)}
                </tr>
                <tr className="border-b border-rule bg-paper/60">
                  <td className="px-6 py-3 text-green sticky left-0 bg-paper">Inflows — collections, advances</td>
                  {INFLOWS.map((v, i) => <td key={i} className="px-4 py-3 text-right text-green">+{fmt(v)}</td>)}
                </tr>
                <tr className="border-b border-rule">
                  <td className="px-6 py-3 text-ink-mid sticky left-0 bg-white">Outflows — payroll, vendors, GST, loans</td>
                  {OUTFLOWS.map((v, i) => <td key={i} className="px-4 py-3 text-right text-ink-mid">−{fmt(v)}</td>)}
                </tr>
                <tr className="bg-forest text-paper">
                  <td className="px-6 py-3 font-medium sticky left-0 bg-forest">Closing balance</td>
                  {closing.map((v, i) => {
                    const crunch = v < 35;
                    return (
                      <td key={i} className={`px-4 py-3 text-right font-medium ${crunch ? "bg-amber/90 text-forest" : "text-mint"}`} data-testid={crunch ? `crunch-week-${i + 1}` : undefined}>
                        {fmt(v)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <FadeUp className="lg:col-span-7 bg-white rounded-2xl p-8">
            <h2 className="font-serif text-2xl text-ink mb-2">AR aging schedule</h2>
            <p className="text-xs text-ink-soft mb-6">Feeds the inflow rows above. Every receivable has an expected collection week — and an owner.</p>
            <div className="overflow-x-auto" data-testid="ar-aging-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-rule text-left font-mono uppercase tracking-wider text-ink-soft">
                    <th className="py-3 pr-4">Client</th>
                    <th className="py-3 pr-4">Invoice</th>
                    <th className="py-3 pr-4">Days</th>
                    <th className="py-3 pr-4 text-right">Amount</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3">Expected</th>
                  </tr>
                </thead>
                <tbody>
                  {AR_ROWS.map((r) => (
                    <tr key={r.invoice} className="border-b border-rule last:border-0">
                      <td className="py-3.5 pr-4 font-medium text-ink">{r.client}</td>
                      <td className="py-3.5 pr-4 font-mono text-ink-mid">{r.invoice}</td>
                      <td className={`py-3.5 pr-4 font-mono ${r.days > 45 ? "text-amber font-medium" : "text-ink-mid"}`}>{r.days}d</td>
                      <td className="py-3.5 pr-4 text-right font-mono text-ink">{r.amount}</td>
                      <td className="py-3.5 pr-4">
                        <span className={`px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-wider ${r.status === "Overdue" ? "bg-amber/15 text-amber" : r.status === "On track" ? "bg-green-lt text-green" : "bg-ink/5 text-ink-mid"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3.5 font-mono text-ink-mid">{r.week}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5">
            <div className="bg-forest grain relative rounded-2xl p-8 text-paper h-full flex flex-col">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint mb-6">How to read it</p>
              <ul className="space-y-5 text-sm text-paper/80 leading-relaxed flex-1">
                <li><span className="text-mint font-medium">01 —</span> Update every Monday. Actuals replace forecasts; the quarter rolls forward.</li>
                <li><span className="text-mint font-medium">02 —</span> Any closing balance below your buffer (here ₹35L) turns amber. That's your early warning.</li>
                <li><span className="text-mint font-medium">03 —</span> Crunch weeks have three levers: pull collections forward, push vendor payments, or arrange bridge credit — in that order.</li>
                <li><span className="text-mint font-medium">04 —</span> Weeks 7–8 above go amber because payroll and GST land together. Now you can act six weeks early.</li>
              </ul>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="cashflow-cta-btn" className="mt-8 inline-flex items-center justify-center gap-2 bg-mint text-forest text-sm font-medium px-6 py-3.5 rounded-sm hover:bg-paper transition-colors duration-200">
                Get this built for your business <ArrowUpRight size={14} />
              </a>
            </div>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
